import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { logStsDebug, serializeUnknownError } from '../auth/okta-debug-log';

/** Normalized STS exchange result used by the app (ABP envelope unwrapped). */
export interface StsTokenExchangeResult {
  accessToken: string;
  expiresIn?: number;
  tokenType?: string;
  userEmail?: string;
  userFullName?: string;
  userId?: number;
}

export const STS_TOKEN_EXCHANGE_GRANT =
  'urn:ietf:params:oauth:grant-type:token-exchange';
export const STS_SUBJECT_TOKEN_TYPE_ID_TOKEN =
  'urn:ietf:params:oauth:token-type:id_token';

/**
 * OAuth token-exchange against gateway STS (`/Auth/ExchangeEventq`).
 * Exchanges an Okta ID token for a Stealth API JWT.
 */
@Injectable({ providedIn: 'root' })
export class StsTokenExchangeService {
  private readonly http = inject(HttpClient);

  exchangeOktaIdToken(oktaIdToken: string): Observable<StsTokenExchangeResult> {
    const trimmed = oktaIdToken.trim();
    if (!trimmed) {
      throw new Error('Okta ID token is required for STS exchange.');
    }

    const body = new FormData();
    body.set('grant_type', STS_TOKEN_EXCHANGE_GRANT);
    body.set('audience', environment.sts.audience);
    body.set('scope', environment.sts.scope);
    body.set('subject_token', trimmed);
    body.set('subject_token_type', STS_SUBJECT_TOKEN_TYPE_ID_TOKEN);

    const requestBody = {
      grant_type: STS_TOKEN_EXCHANGE_GRANT,
      audience: environment.sts.audience,
      scope: environment.sts.scope,
      subject_token: trimmed,
      subject_token_type: STS_SUBJECT_TOKEN_TYPE_ID_TOKEN,
    };

    const url = environment.sts.exchangePath;
    logStsDebug('StsTokenExchangeService.exchangeOktaIdToken: POST body + token', {
      url,
      method: 'POST',
      contentType: 'multipart/form-data (FormData)',
      requestBody,
      subject_token: trimmed,
      subjectTokenLength: trimmed.length,
    });

    return this.http
      .post<unknown>(url, body, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
      })
      .pipe(
        tap({
          next: (response) => {
            console.info('[STS] ExchangeEventq response', redactStsResponse(response));
            logStsDebug('StsTokenExchangeService.exchangeOktaIdToken: response', {
              redacted: redactStsResponse(response),
            });
          },
          error: (error: unknown) => {
            const http = error instanceof HttpErrorResponse ? error : null;
            logStsDebug('StsTokenExchangeService.exchangeOktaIdToken: error', {
              status: http?.status,
              statusText: http?.statusText,
              url: http?.url,
              error: serializeUnknownError(error),
              redactedBody: http ? redactStsResponse(http.error) : undefined,
            });
          },
        }),
        map((response) => unwrapStsExchangeResponse(response)),
      );
  }
}

/** Supports flat OAuth JSON or ABP `{ result: { access_token | accessToken, … } }`. */
export function unwrapStsExchangeResponse(response: unknown): StsTokenExchangeResult {
  const root = asRecord(response);
  if (!root) {
    throw new Error('STS exchange returned an empty body.');
  }

  const payload = asRecord(root['result']) ?? root;
  const accessToken =
    readString(payload, 'access_token') ?? readString(payload, 'accessToken');
  if (!accessToken) {
    throw new Error('STS exchange returned no access_token.');
  }

  const expiresIn =
    readNumber(payload, 'expires_in') ?? readNumber(payload, 'expireInSeconds');

  return {
    accessToken,
    expiresIn: expiresIn ?? undefined,
    tokenType: readString(payload, 'token_type') ?? readString(payload, 'tokenType'),
    userEmail: readString(payload, 'userEmail') ?? readString(payload, 'email'),
    userFullName: readString(payload, 'userFullName') ?? readString(payload, 'name'),
    userId: readNumber(payload, 'userId') ?? undefined,
  };
}

/** Logs STS shape without printing bearer/JWT values (recursive). */
export function redactStsResponse(value: unknown): unknown {
  const secretKeys = new Set([
    'access_token',
    'accessToken',
    'encryptedAccessToken',
    'refresh_token',
    'refreshToken',
    'id_token',
    'subject_token',
    'actor_token',
  ]);

  if (Array.isArray(value)) {
    return value.map((item) => redactStsResponse(item));
  }
  if (!value || typeof value !== 'object') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const redacted: Record<string, unknown> = { keys: Object.keys(record) };
  for (const [key, nested] of Object.entries(record)) {
    if (typeof nested === 'string' && secretKeys.has(key)) {
      redacted[key] = { present: nested.length > 0, length: nested.length };
    } else if (nested && typeof nested === 'object') {
      redacted[key] = redactStsResponse(nested);
    } else {
      redacted[key] = nested;
    }
  }
  return redacted;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
